import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, FileText, AlertCircle, CheckCircle2,
  Clock, Download, RefreshCw, ChevronRight, BarChart2, Shield
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import billingService from '../../../services/billingService';
import toast from 'react-hot-toast';

const fmt = (n) => (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const GST_RETURNS = [
  { id: 'gstr1', name: 'GSTR-1', desc: 'Outward supplies detail', path: '/admin/gst/gstr1', color: 'blue', due: '11th of next month' },
  { id: 'gstr3b', name: 'GSTR-3B', desc: 'Monthly summary return', path: '/admin/gst/gstr3b', color: 'purple', due: '20th of next month' },
  { id: 'gstr2ab', name: 'GSTR-2A/2B', desc: 'Auto-drafted inward supplies', path: '/admin/gst/gstr2ab', color: 'teal', due: 'Auto-generated' },
  { id: 'reconciliation', name: 'Reconciliation', desc: 'Match purchase register vs 2B', path: '/admin/gst/reconciliation', color: 'orange', due: 'Before filing' },
  { id: 'itc', name: 'ITC Management', desc: 'Input Tax Credit tracking', path: '/admin/gst/itc', color: 'emerald', due: 'Ongoing' },
  { id: 'reports', name: 'GST Reports', desc: 'Tax summary, HSN, rate-wise', path: '/admin/gst/reports', color: 'indigo', due: 'On demand' },
];

export default function GstDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportModal, setExportModal] = useState(false);
  const [exportForm, setExportForm] = useState({
    month: String(new Date().getMonth() + 1).padStart(2, '0'),
    year: String(new Date().getFullYear())
  });

  useEffect(() => {
    billingService.getGstTaxSummary()
      .then(res => setSummary(res.data))
      .catch(() => toast.error('Failed to load GST summary'))
      .finally(() => setLoading(false));
  }, []);

  const outward = summary?.outwardSupplies || {};
  const inward  = summary?.inwardSupplies  || {};
  const net     = (outward.totalTax || 0) - (inward.totalTax || 0);

  const cards = [
    { label: 'Outward Tax Liability', value: outward.totalTax || 0, icon: TrendingUp, color: 'red', sub: `Taxable: ₹${fmt(outward.totalTaxable)}` },
    { label: 'Inward ITC Available', value: inward.totalTax || 0, icon: TrendingDown, color: 'emerald', sub: `Purchases: ₹${fmt(inward.totalTaxable)}` },
    { label: 'Net Tax Payable', value: Math.max(0, net), icon: Shield, color: 'purple', sub: net < 0 ? `Excess ITC: ₹${fmt(Math.abs(net))}` : 'After ITC set-off' },
    { label: 'CGST Liability', value: outward.totalCgst || 0, icon: BarChart2, color: 'blue', sub: `SGST: ₹${fmt(outward.totalSgst || 0)}` },
  ];

  const now = new Date();
  const months = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: new Date(2000, i).toLocaleString('en-IN', { month: 'long' }) }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">GST Dashboard</h1>
          <p className="text-xs text-slate-500 font-medium">GSTIN: 33AAAPL1234A1Z5 · Tamil Nadu · Regular Taxpayer</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/admin/gst/settings')}
            className="px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
            GST Settings
          </button>
          <button onClick={() => setExportModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-purple-600 hover:bg-purple-700 text-white shadow-sm">
            <Download className="w-3.5 h-3.5" /> Export Returns
          </button>
        </div>
      </div>

      {/* Tax Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {loading ? Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
            <div className="h-4 bg-slate-100 rounded-full w-3/4 mb-3" />
            <div className="h-6 bg-slate-100 rounded-full w-1/2" />
          </div>
        )) : cards.map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-${card.color}-50 mb-3`}>
              <card.icon className={`w-4 h-4 text-${card.color}-600`} />
            </div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{card.label}</p>
            <p className="text-lg font-black text-slate-900 font-mono mt-0.5">₹{fmt(card.value)}</p>
            <p className="text-[10px] text-slate-400 font-medium mt-1">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* GST Return Modules */}
      <div>
        <h2 className="text-xs font-black text-slate-500 uppercase tracking-wider mb-3">GST Return Modules</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {GST_RETURNS.map(r => (
            <button key={r.id} onClick={() => navigate(r.path)}
              className={`bg-white border border-slate-200 rounded-2xl p-5 text-left hover:shadow-md hover:border-${r.color}-200 transition-all group`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`px-2.5 py-1 rounded-lg bg-${r.color}-100 text-${r.color}-700 text-xs font-black uppercase tracking-wider`}>
                  {r.name}
                </div>
                <ChevronRight className={`w-4 h-4 text-slate-300 group-hover:text-${r.color}-500 transition-colors`} />
              </div>
              <p className="font-semibold text-sm text-slate-900">{r.desc}</p>
              <p className="text-[10px] text-slate-400 font-medium mt-1">Due: {r.due}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Export Modal */}
      {exportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-900 rounded-t-2xl">
              <h3 className="font-bold text-sm text-white">Export GST Returns</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Month</label>
                  <select value={exportForm.month} onChange={e => setExportForm(f => ({ ...f, month: e.target.value }))}
                    className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50">
                    {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Year</label>
                  <select value={exportForm.year} onChange={e => setExportForm(f => ({ ...f, year: e.target.value }))}
                    className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50">
                    {[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <button onClick={() => { billingService.downloadGstr1Json({ month: exportForm.month, year: exportForm.year }); toast.success('Downloading GSTR-1 JSON…'); }}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs">
                  <Download className="w-4 h-4" /> Download GSTR-1 JSON (GST Portal Format)
                </button>
                <button onClick={() => { billingService.downloadPurchaseJson({ month: exportForm.month, year: exportForm.year }); toast.success('Downloading Purchase JSON…'); }}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs">
                  <Download className="w-4 h-4" /> Download Purchase Register JSON
                </button>
                <button onClick={() => { billingService.downloadGstExcelReport({ month: exportForm.month, year: exportForm.year }); toast.success('Downloading Excel…'); }}
                  className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs">
                  <Download className="w-4 h-4" /> Download GST Excel Report
                </button>
              </div>
            </div>
            <div className="px-6 pb-5">
              <button onClick={() => setExportModal(false)} className="w-full py-2.5 text-xs font-bold rounded-xl border border-slate-200 text-slate-600">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
